import { baseApi } from "@/shared/api";
import { decrementPendingActionsCount } from "@/features/auth";
import { authApi } from "@/features/auth/api";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import type { CreateAmendmentDto, CreateAmendmentResponse, UpdateAmendmentDto, AmendmentDetailResponse } from "@/features/hr/amendments/types";

export const selfServiceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createSelfAmendment: builder.mutation<CreateAmendmentResponse, CreateAmendmentDto>({
      query: (body) => ({
        url: "/api/self/amendments/create/",
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: [
        { type: "SelfService" as const, id: "REQUESTS" },
        { type: "Amendments" as const, id: "LIST" },
        { type: "Contracts" as const, id: "LIST" },
        "PendingActions",
      ],
    }),
    updateSelfAmendment: builder.mutation<AmendmentDetailResponse, { id: number; data: UpdateAmendmentDto }>({
      query({ id, data }) {
        return {
          url: `/api/self/amendments/${id}/`,
          method: "PATCH",
          body: data,
          credentials: "include",
        };
      },
      invalidatesTags: [
        { type: "SelfService" as const, id: "REQUESTS" },
        { type: "Amendments" as const, id: "LIST" },
        "PendingActions",
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) => item.action_required === "revise_application" && item.id === id,
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),
    deleteSelfAmendment: builder.mutation<void, number>({
      query(id) {
        return {
          url: `/api/self/amendments/${id}/`,
          method: "DELETE",
          credentials: "include",
        };
      },
      invalidatesTags: [
        { type: "SelfService" as const, id: "REQUESTS" },
        { type: "Amendments" as const, id: "LIST" },
      ],
    }),
  }),
});

export const {
  useCreateSelfAmendmentMutation,
  useUpdateSelfAmendmentMutation,
  useDeleteSelfAmendmentMutation,
} = selfServiceApi;
